'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

const LeafIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
  </svg>
);

interface FormState {
  name: string;
  email: string;
  sender_type: string;
  subject: string;
  message: string;
}

const INITIAL_FORM: FormState = {
  name: '',
  email: '',
  sender_type: '',
  subject: '',
  message: '',
};

export default function ContactPage() {
  const supabase = createClient();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    const { error } = await supabase.from('contact_messages').insert({
      name: form.name.trim(),
      email: form.email.trim(),
      sender_type: form.sender_type,
      subject: form.subject.trim(),
      message: form.message.trim(),
    });

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
    } else {
      setStatus('success');
      setForm(INITIAL_FORM);
    }
  };

  return (
    <div className="min-h-screen grain bg-eco-bg">
      {/* Header */}
      <header className="max-w-7xl mx-auto px-5 sm:px-6 py-5 sm:py-6 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-emerald-600 grid place-items-center text-white shadow-md">
            <LeafIcon className="w-5 h-5" />
          </div>
          <div className="font-display text-xl sm:text-2xl text-emerald-900">We Are Circular</div>
        </Link>
        <div className="flex items-center gap-2">
          <Link href="/login" className="px-4 py-2 rounded-xl text-emerald-800 hover:bg-emerald-50 text-sm font-medium transition-colors">Sign In</Link>
          <Link href="/register" className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors">Join Now</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-5 sm:px-6 pt-12 pb-16 text-center">
        <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 text-xs font-bold px-3 py-1.5 rounded-full mb-6 uppercase tracking-widest">
          <LeafIcon className="w-3.5 h-3.5" />
          Get In Touch
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-emerald-900 leading-tight mb-6">
          We Would Love to <br className="hidden sm:block" />
          <span className="text-emerald-600">Hear From You</span>
        </h1>
        <p className="text-slate-600 text-lg leading-relaxed max-w-2xl mx-auto">
          Whether you are a school looking to join, a volunteer wanting to help, or a partner interested in collaboration — reach out and we will get back to you within 24 hours.
        </p>
      </section>

      {/* Contact Cards */}
      <section className="max-w-5xl mx-auto px-5 sm:px-6 pb-16">
        <div className="grid sm:grid-cols-3 gap-5 mb-12">
          {[
            {
              emoji: '📧',
              title: 'Email Us',
              detail: 'wecircular26@gmail.com',
              sub: 'We reply within 24 hours',
              href: 'mailto:wecircular26@gmail.com',
              cta: 'Send Email',
              color: 'bg-blue-50 border-blue-200',
            },
            {
              emoji: '📱',
              title: 'WhatsApp',
              detail: '+91 84316 54959',
              sub: 'Mon–Sat, 9 AM – 6 PM IST',
              href: 'https://wa.me/918431654959',
              cta: 'Message Us',
              color: 'bg-emerald-50 border-emerald-200',
            },
            {
              emoji: '📍',
              title: 'Our Office',
              detail: 'Mysuru, Karnataka',
              sub: 'India – 570001',
              href: 'https://maps.google.com/?q=Mysuru,Karnataka',
              cta: 'View on Map',
              color: 'bg-amber-50 border-amber-200',
            },
          ].map((c) => (
            <div key={c.title} className={`eco-card p-6 border-2 ${c.color} text-center`}>
              <div className="text-4xl mb-3">{c.emoji}</div>
              <h3 className="font-display text-lg text-emerald-900 mb-1">{c.title}</h3>
              <p className="font-semibold text-slate-800 text-sm mb-1">{c.detail}</p>
              <p className="text-slate-500 text-xs mb-4">{c.sub}</p>
              <a
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-5 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors"
              >
                {c.cta}
              </a>
            </div>
          ))}
        </div>

        {/* Contact Form */}
        <div className="eco-card p-8 max-w-2xl mx-auto">
          <h2 className="font-display text-2xl text-emerald-900 mb-2">Send Us a Message</h2>
          <p className="text-slate-500 text-sm mb-6">Fill in the form below and we will get back to you as soon as possible.</p>

          {status === 'success' ? (
            <div className="text-center py-10">
              <div className="text-5xl mb-4">✅</div>
              <h3 className="font-display text-xl text-emerald-900 mb-2">Message Sent!</h3>
              <p className="text-slate-500 text-sm mb-6">Thank you for reaching out. We will get back to you within 24 hours.</p>
              <button
                onClick={() => setStatus('idle')}
                className="px-6 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Your full name"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">I am a…</label>
                <select
                  name="sender_type"
                  value={form.sender_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                >
                  <option value="">Select your role</option>
                  <option value="school">School / Principal</option>
                  <option value="teacher">Teacher</option>
                  <option value="student">Student</option>
                  <option value="parent">Parent</option>
                  <option value="volunteer">Volunteer</option>
                  <option value="ngo">NGO / Partner</option>
                  <option value="media">Media / Press</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Subject *</label>
                <input
                  type="text"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  required
                  placeholder="How can we help?"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Message *</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Tell us more about your query, school, or how you would like to get involved…"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                />
              </div>

              {status === 'error' && (
                <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  {errorMsg || 'Something went wrong. Please try again.'}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full py-3 bg-emerald-600 text-white rounded-xl font-semibold text-sm hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? 'Sending…' : 'Send Message'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* For Schools */}
      <section className="bg-white/60 backdrop-blur-sm border-y border-emerald-100 py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl text-emerald-900 mb-3">Is Your School in Mysuru?</h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              We are actively onboarding schools across Mysuru. If your school is not yet registered, we would love to connect.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { emoji: '📋', title: 'Register Your School', desc: 'Sign up as a School Admin and get your institution listed on the platform in under 24 hours.' },
              { emoji: '🎓', title: 'Train Your Volunteers', desc: 'We provide free training materials and onboarding support for all school volunteers.' },
              { emoji: '📊', title: 'Get Monthly Reports', desc: 'Receive detailed impact reports showing your school\'s waste collection, Eco Points, and CO₂ savings.' },
            ].map((item) => (
              <div key={item.title} className="eco-card p-6 text-center">
                <div className="text-3xl mb-3">{item.emoji}</div>
                <h3 className="font-display text-base text-emerald-900 mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link href="/register" className="inline-block px-8 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors">
              Register Your School
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-emerald-100 bg-white/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 py-8 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-emerald-600 grid place-items-center text-white">
              <LeafIcon className="w-4 h-4" />
            </div>
            <span className="font-display text-emerald-900">We Are Circular</span>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link href="/" className="hover:text-emerald-700 transition-colors">Home</Link>
            <Link href="/about" className="hover:text-emerald-700 transition-colors">About Us</Link>
            <Link href="/how-it-works" className="hover:text-emerald-700 transition-colors">How It Works</Link>
            <Link href="/contact" className="hover:text-emerald-700 transition-colors">Contact</Link>
            <Link href="/terms" className="hover:text-emerald-700 transition-colors">Terms</Link>
          </div>
          <div>© 2026 We Are Circular. Built with ❤️ by Pankaj &amp; Misha Arya</div>
        </div>
      </footer>
    </div>
  );
}
